import { create } from "zustand";
import type { AgentFocus } from "@/types/agentFocus.type";

/**
 * What the brand agent is doing right now, for pages that show the work on
 * themselves rather than in the chat (Strategy: "Creo is writing chapter 4").
 *
 * Written by useBrandAgentChat from the stream it already parses: a turn
 * starting and ending, and each write tool's start and end. `tool_start`
 * carries the tool's input, so the chapter being written is known while the
 * call runs; the strategy is the one the turn was sent from (the backend binds
 * WriteStrategyChapter to the focus the message carried).
 */
export interface AgentStrategyWrite {
  toolCallId: string;
  strategyId: string | null;
  /** The chapter the call writes ("brief", "content"…). */
  chapter: string;
}

interface AgentRunStore {
  /** A turn is streaming. */
  running: boolean;
  /** The focus the running turn was sent with. */
  focus: AgentFocus | null;
  /** The strategy chapter being written, while WriteStrategyChapter runs. */
  strategyWrite: AgentStrategyWrite | null;
  /** The strategy's last chapter write (to animate what landed). */
  landedAt: number;
  start: (focus: AgentFocus) => void;
  toolStart: (toolName: string, toolCallId: string, input: unknown) => void;
  toolEnd: (toolName: string, toolCallId: string | undefined) => void;
  end: () => void;
}

export const useAgentRunStore = create<AgentRunStore>()((set, get) => ({
  running: false,
  focus: null,
  strategyWrite: null,
  landedAt: 0,
  start: (focus) => set({ running: true, focus, strategyWrite: null }),
  toolStart: (toolName, toolCallId, input) => {
    if (toolName !== "WriteStrategyChapter") return;
    const chapter = (input as { chapter?: unknown } | null)?.chapter;
    if (typeof chapter !== "string") return;
    const focus = get().focus;
    set({
      strategyWrite: {
        toolCallId,
        chapter,
        strategyId: focus?.surface === "strategy" ? (focus.strategyId ?? null) : null,
      },
    });
  },
  toolEnd: (toolName, toolCallId) => {
    if (toolName !== "WriteStrategyChapter") return;
    const current = get().strategyWrite;
    if (current && (!toolCallId || current.toolCallId === toolCallId)) {
      set({ strategyWrite: null, landedAt: Date.now() });
    }
  },
  end: () => set({ running: false, focus: null, strategyWrite: null }),
}));
